import { useMemo, useState } from "react";
import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import { Button, CheckboxField, Field, SelectField, StatusBadge, Tabs, type Column } from "@fawkes/ui";
import { formatDate, formatEuro, todayIso } from "../i18n/format";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

/**
 * Vergoedingen: de afspraken met verwijzende praktijken en derde partijen, over alle relaties heen.
 *
 * <p>De gegevens wonen op de relatie (het tabblad Dierenarts / Derde partij, met versiegeschiedenis).
 * Dit scherm draait het om: niet "wat hebben we met deze praktijk afgesproken" maar "wie krijgt wat,
 * en wat betalen we deze maand uit". Wijzigen gebeurt daarom op de relatie; hier alleen kijken en
 * de uitbetalingslijst trekken.
 */

type TabKey = "afspraken" | "uitbetaling";

interface Afspraak {
  id: number;
  relatieId: number;
  relatieNaam: string;
  rolType: string;
  vergoedingstype: string | null;
  vergoeding: number | null;
  vervoersprijs: number | null;
  btwPercentage: number | null;
  uitbetaling: string | null;
  ingangsdatum: string | null;
  status: "ACTIEF" | "GEPLAND";
  interneOpmerking: string | null;
}

interface UitbetalingRegel {
  relatieId: number;
  relatieNaam: string | null;
  aantalUitvaarten: number;
  totaal: number;
}

/** De eerste en laatste dag van de maand van `iso` — de standaardperiode van de uitbetalingslijst. */
function maandVan(iso: string): { van: string; tot: string } {
  const [j, m] = iso.split("-").map(Number);
  const laatste = new Date(Date.UTC(j, m, 0)).getUTCDate();
  const mm = String(m).padStart(2, "0");
  return { van: `${j}-${mm}-01`, tot: `${j}-${mm}-${String(laatste).padStart(2, "0")}` };
}

export function CrematoriumVergoedingen({ onOpenRelatie }: { onOpenRelatie?: (relatieId: number) => void }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [tab, setTab] = useState<TabKey>("afspraken");

  // ── filters van de afsprakenlijst ──
  const [zoek, setZoek] = useState("");
  const [rol, setRol] = useState("");
  const [peildatum, setPeildatum] = useState(todayIso());
  const [alleenActief, setAlleenActief] = useState(true);

  const afsprakenUrl = useMemo(() => {
    const p = new URLSearchParams();
    if (zoek.trim()) p.set("q", zoek.trim());
    if (rol) p.set("rol", rol);
    if (peildatum) p.set("peildatum", peildatum);
    p.set("alleenActief", String(alleenActief));
    return `crematorium/vergoedingen?${p.toString()}`;
  }, [zoek, rol, peildatum, alleenActief]);
  const afspraken = useCustom<Afspraak[]>({ url: afsprakenUrl, method: "get" });
  const rijen = Array.isArray(afspraken.data?.data) ? afspraken.data!.data : [];

  // ── uitbetalingslijst ──
  const [periode, setPeriode] = useState(() => maandVan(todayIso()));
  const uitbetaling = useCustom<UitbetalingRegel[]>({
    url: `crematorium/vergoedingen/uitbetaling?van=${periode.van}&tot=${periode.tot}`,
    method: "get",
    queryOptions: { enabled: tab === "uitbetaling" },
  });
  const uitRijen = Array.isArray(uitbetaling.data?.data) ? uitbetaling.data!.data : [];
  const uitTotaal = uitRijen.reduce((s, r) => s + (r.totaal ?? 0), 0);

  const rolLabel = (r: string) =>
    r === "DIERENARTS" ? t("crematoriumVergoedingen.rol.praktijk")
      : r === "DERDE_PARTIJ" ? t("crematoriumVergoedingen.rol.derdePartij") : r;

  const afspraakKolommen: Column<Afspraak>[] = [
    {
      header: t("crematoriumVergoedingen.col.relatie"),
      cell: (r) => (
        <button type="button" className="fawkes-link" onClick={() => onOpenRelatie?.(r.relatieId)}>
          {r.relatieNaam}
        </button>
      ),
    },
    { header: t("crematoriumVergoedingen.col.rol"), cell: (r) => rolLabel(r.rolType) },
    {
      header: t("crematoriumVergoedingen.col.type"),
      cell: (r) => r.vergoedingstype === "PERCENTAGE"
        ? t("crematoriumVergoedingen.type.percentage")
        : t("crematoriumVergoedingen.type.bedrag"),
    },
    {
      header: t("crematoriumVergoedingen.col.waarde"),
      cell: (r) => r.vergoeding == null ? "—"
        : r.vergoedingstype === "PERCENTAGE" ? `${r.vergoeding} %` : formatEuro(r.vergoeding, locale),
    },
    {
      header: t("crematoriumVergoedingen.col.vervoer"),
      cell: (r) => (r.vervoersprijs == null ? "—" : formatEuro(r.vervoersprijs, locale)),
    },
    {
      header: t("crematoriumVergoedingen.col.uitbetaling"),
      cell: (r) => r.uitbetaling === "KWARTAAL" ? t("crematoriumVergoedingen.uitbetaling.kwartaal")
        : t("crematoriumVergoedingen.uitbetaling.maand"),
    },
    {
      header: t("crematoriumVergoedingen.col.geldigVanaf"),
      cell: (r) => (r.ingangsdatum ? formatDate(r.ingangsdatum, locale) : "—"),
    },
    {
      header: t("crematoriumVergoedingen.col.status"),
      cell: (r) => (
        <StatusBadge tone={r.status === "ACTIEF" ? "success" : "info"}>
          {r.status === "ACTIEF" ? t("crematoriumVergoedingen.status.actief") : t("crematoriumVergoedingen.status.gepland")}
        </StatusBadge>
      ),
    },
  ];

  const uitKolommen: Column<UitbetalingRegel>[] = [
    {
      header: t("crematoriumVergoedingen.col.relatie"),
      cell: (r) => (
        <button type="button" className="fawkes-link" onClick={() => onOpenRelatie?.(r.relatieId)}>
          {r.relatieNaam ?? `#${r.relatieId}`}
        </button>
      ),
    },
    { header: t("crematoriumVergoedingen.col.uitvaarten"), cell: (r) => String(r.aantalUitvaarten) },
    { header: t("crematoriumVergoedingen.col.totaal"), cell: (r) => formatEuro(r.totaal, locale) },
  ];

  return (
    <div className="crem-page">
      <div className="crem-page__head">
        <div className="crem-page__intro">
          <div>
            <h2 className="crem-page__title">{t("crematoriumVergoedingen.title")}</h2>
            <p className="crem-page__sub">{t("crematoriumVergoedingen.sub")}</p>
          </div>
        </div>
      </div>

      <Tabs
        tabs={[
          { key: "afspraken", label: t("crematoriumVergoedingen.tab.afspraken") },
          { key: "uitbetaling", label: t("crematoriumVergoedingen.tab.uitbetaling") },
        ]}
        activeKey={tab}
        onChange={(k) => setTab(k as TabKey)}
        ariaLabel={t("crematoriumVergoedingen.title")}
      />

      {tab === "afspraken" && (
        <div className="asiel-tarief__panel">
          <div className="asiel-tarief__toolbar">
            <Field
              label={t("crematoriumVergoedingen.zoek")}
              placeholder={t("crematoriumVergoedingen.zoek")}
              value={zoek}
              onChange={(e) => setZoek(e.target.value)}
            />
            <SelectField
              label={t("crematoriumVergoedingen.col.rol")}
              value={rol}
              onChange={(e) => setRol(e.target.value)}
              options={[
                { value: "", label: t("crematoriumVergoedingen.rol.alle") },
                { value: "DIERENARTS", label: t("crematoriumVergoedingen.rol.praktijk") },
                { value: "DERDE_PARTIJ", label: t("crematoriumVergoedingen.rol.derdePartij") },
              ]}
            />
            <Field
              label={t("crematoriumVergoedingen.peildatum")}
              type="date"
              value={peildatum}
              onChange={(e) => setPeildatum(e.target.value)}
            />
            <CheckboxField
              label={t("crematoriumVergoedingen.alleenActief")}
              checked={alleenActief}
              onChange={(e) => setAlleenActief(e.target.checked)}
            />
          </div>
          <p className="fawkes-muted">{t("crematoriumVergoedingen.uitleg")}</p>
          <IndeelbareTabel<Afspraak> tabelKey="crematorium-vergoedingen-afspraak"
            columns={afspraakKolommen}
            rows={rijen}
            getRowKey={(r) => String(r.id)}
            empty={afspraken.isLoading ? t("common.loading") : t("crematoriumVergoedingen.empty")}
          />
        </div>
      )}

      {tab === "uitbetaling" && (
        <div className="asiel-tarief__panel">
          <div className="asiel-tarief__toolbar">
            <Field
              label={t("crematoriumVergoedingen.van")}
              type="date"
              value={periode.van}
              onChange={(e) => setPeriode((p) => ({ ...p, van: e.target.value }))}
            />
            <Field
              label={t("crematoriumVergoedingen.tot")}
              type="date"
              value={periode.tot}
              onChange={(e) => setPeriode((p) => ({ ...p, tot: e.target.value }))}
            />
            <Button variant="secondary" onClick={() => setPeriode(maandVan(todayIso()))}>
              {t("crematoriumVergoedingen.dezeMaand")}
            </Button>
          </div>
          <p className="fawkes-muted">{t("crematoriumVergoedingen.uitbetaling.uitleg")}</p>
          <IndeelbareTabel<UitbetalingRegel> tabelKey="crematorium-vergoedingen-uitbetaling-regel"
            columns={uitKolommen}
            rows={uitRijen}
            getRowKey={(r) => String(r.relatieId)}
            empty={uitbetaling.isLoading ? t("common.loading") : t("crematoriumVergoedingen.uitbetaling.empty")}
          />
          {uitRijen.length > 0 && (
            <p className="crem-totaal" data-testid="uitbetaling-totaal">
              {t("crematoriumVergoedingen.uitbetaling.totaal")}: <strong>{formatEuro(uitTotaal, locale)}</strong>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
